/**
 * Zod-схемы модуля `points` — единственного эмитента Баллов (инвариант №9).
 * Источники: docs/DOMAIN-MODEL.md §6, план §4 (валюта, ledger, гранты), §8 (вехи).
 *
 * ЗАКРЫТЫЙ КОНТУР (инвариант №1). В контракте намеренно НЕТ и не может появиться:
 *   — вывода / обналичивания (`withdraw`, `payout`, `cashout`);
 *   — перевода Баллов другому пользователю (нет поля «получатель» ни в одной схеме);
 *   — покупки Баллов (нет входа «деньги → Баллы»).
 * Единственный путь Баллов «наружу» — грант-купон: списание у нас и скидка на услугу
 * нашего же юрлица (`GrantScopeSchema.ownServicesOnly` — литерал `true`).
 *
 * Все числа берутся из `constants/points.ts`; магических констант здесь нет.
 */
import { z } from "zod";
import { POINTS_ENTRY_KINDS } from "../../constants/points.js";
import { IEC_PLUS_TIERS } from "../../constants/subscription.js";
import {
	CurrencySchema,
	IsoDateTimeSchema,
	MoneyMinorSchema,
	PaginationInputSchema,
	PointsAmountSchema,
	paginated,
	ServiceIdSchema,
	SubjectSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";

// ───────────────────────────── Словари ─────────────────────────────

/** Вид записи ledger. Раздельные `kind` нужны, чтобы считать бюджеты (план §4.4). */
export const PointsEntryKindSchema = z.enum(POINTS_ENTRY_KINDS);

/**
 * Класс источника эмиссии (план §4.2): `a` — «привёл платящего» (процент от денег),
 * `b` — «был активен» (суточный потолок + бюджет 5%), `milestone` — вехи обучения
 * (ограничены конструкцией `UNIQUE`, а не дросселем — план §8).
 */
export const PointsSourceClassSchema = z.enum(["a", "b", "milestone"]);

/** Уровень членства: от него зависят суточный потолок и множитель начислений. */
export const PointsTierSchema = z.enum(IEC_PLUS_TIERS);

/** Баланс не бывает отрицательным (списание больше баланса отклоняется до записи). */
export const PointsBalanceSchema = PointsAmountSchema.nonnegative();

/** Количество Баллов в операции — всегда положительное целое. */
export const PointsPositiveSchema = PointsAmountSchema.positive();

/** Ключ идемпотентности межсервисного вызова (инвариант «идемпотентность», §4.4). */
export const IdempotencyKeySchema = z.string().min(8).max(128);

// ──────────────────────────── points.summary ────────────────────────────

/** Ближайшее сгорание: сколько Баллов и когда (витрина показывает дату — план §4.1). */
export const PointsExpirySchema = z.object({
	points: PointsPositiveSchema,
	expiresAt: IsoDateTimeSchema,
});

/** Суточный кран класса B: потолок уровня, сколько израсходовано сегодня. */
export const PointsDailyCapSchema = z.object({
	tier: PointsTierSchema,
	limitPoints: PointsPositiveSchema,
	usedTodayPoints: PointsBalanceSchema,
	remainingPoints: PointsBalanceSchema,
	/** Множитель начислений класса B и вех для подписчиков IEC+. */
	multiplier: z.number().int().min(1),
});

export const PointsSummaryOutputSchema = z.object({
	balance: PointsBalanceSchema,
	/** Стоимость баланса по фиксированному курсу, minor units. */
	worthMinor: MoneyMinorSchema.nonnegative(),
	currency: CurrencySchema,
	lifetimeEarned: PointsBalanceSchema,
	lifetimeSpent: PointsBalanceSchema,
	/** `null`, если сгорать нечему. */
	nextExpiry: PointsExpirySchema.nullable(),
	dailyCap: PointsDailyCapSchema,
	updatedAt: IsoDateTimeSchema,
});

// ──────────────────────────── points.history ────────────────────────────

/**
 * Чип-пояснение под суммой в ленте: «10% of $1 400» (`rate`), «milestone» /
 * «spent» / «activation» (`label`). Текст — через i18n-ключ, не строкой с сервера.
 */
export const PointsEntryBadgeSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("rate"),
		pct: z.number().int().min(0).max(100),
		ofMinor: MoneyMinorSchema.nonnegative(),
		currency: CurrencySchema,
	}),
	z.object({ type: z.literal("label"), labelKey: z.string().min(1).max(128) }),
	z.object({ type: z.literal("none") }),
]);

export const PointsHistoryEntrySchema = z.object({
	id: UuidV7Schema,
	/** Знак несёт направление операции: `+` начисление, `−` списание/сгорание. */
	amount: PointsAmountSchema,
	balanceAfter: PointsBalanceSchema,
	kind: PointsEntryKindSchema,
	/** `null` для служебных видов (`expire`, `migrate`, `adjust`). */
	sourceClass: PointsSourceClassSchema.nullable(),
	/** Где произошло событие; `null` — операция самого IEC ID. */
	serviceId: ServiceIdSchema.nullable(),
	reasonCode: z.string().min(1).max(64),
	/**
	 * i18n-ключ заголовка строки (`points.history.kind.<kind>`) и параметры ICU.
	 *
	 * ИМЁН И НАЗВАНИЙ В ПАРАМЕТРАХ НЕТ. Прежняя редакция обещала «имя
	 * приглашённого» — обещание снято, а не реализовано: ledger append-only и при
	 * удалении аккаунта только анонимизируется (инвариант №11), поэтому имя,
	 * записанное в `meta` ради заголовка, пережило бы анонимизацию и осталось бы
	 * персональными данными внутри финансовой записи.
	 *
	 * Что параметры несут: `amount`, `reasonCode`, скалярные поля `meta` и три
	 * НОРМАЛИЗОВАННЫХ признака, гарантированных для любой строки, —
	 * `targetType` (цель погашения), `milestoneScope` (веха реферальная или
	 * учебная) и `achievementKey` (код достижения, иначе `other`). На них стоит
	 * ICU-`select`, и отсутствие любого сломало бы рендер строки сразу на всех
	 * локалях, поэтому их проставляет сам маппер.
	 */
	titleKey: z.string().min(1).max(128),
	titleParams: z.record(z.string(), z.union([z.string(), z.number()])),
	badge: PointsEntryBadgeSchema,
	/** Когда сгорит эта конкретная порция (для начислений); `null` — не сгорает. */
	expiresAt: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});

export const PointsHistoryInputSchema = PaginationInputSchema.extend({
	serviceId: ServiceIdSchema.optional(),
	kind: z.array(PointsEntryKindSchema).min(1).max(POINTS_ENTRY_KINDS.length).optional(),
	/** Табы витрины: вся лента / только начисления / только списания. */
	/**
	 * Направление операций. Именно `flow`, а не `direction`: последнее —
	 * зарезервированный ключ бесконечных запросов TanStack Query, и он молча
	 * подменял значение фильтра на «forward» при подгрузке следующей страницы.
	 */
	flow: z.enum(["all", "earn", "spend"]).default("all"),
});

export const PointsHistoryOutputSchema = paginated(PointsHistoryEntrySchema);

// ─────────────────────────── points.earnGuide ───────────────────────────

/** Награда правила: фиксированная сумма (класс B, вехи) или процент (класс A). */
export const PointsRewardSchema = z.discriminatedUnion("type", [
	z.object({ type: z.literal("fixed"), points: PointsPositiveSchema }),
	z.object({
		type: z.literal("percent"),
		pct: z.number().int().min(0).max(100),
		/** От чего считается процент — от нашей части денег, не от всего чека. */
		base: z.enum(["programme", "subscription_first", "subscription_renewal", "course"]),
	}),
]);

export const PointsEarnRuleSchema = z.object({
	/** Ключ правила = i18n-ключ заголовка и пояснения. */
	key: z.string().min(1).max(128),
	sourceClass: PointsSourceClassSchema,
	serviceId: ServiceIdSchema.nullable(),
	reward: PointsRewardSchema,
	/** Действующий множитель (IEC+ ×3 для класса B и вех; для класса A всегда 1). */
	multiplier: z.number().int().min(1),
	/** Потолок класса B; `null` для класса A и вех — их ограничивает конструкция. */
	dailyCapPoints: PointsPositiveSchema.nullable(),
	/** Доступно ли правило пользователю прямо сейчас (например, сервис не подключён). */
	available: z.boolean(),
});

export const PointsEarnGuideOutputSchema = z.object({
	rules: z.array(PointsEarnRuleSchema),
	tier: PointsTierSchema,
	dailyCap: PointsDailyCapSchema,
	/**
	 * Бюджет класса B исчерпан выше порога и коэффициенты снижены (план §4.2).
	 * Показываем честно — деградация громкая (инвариант №14).
	 */
	classBThrottled: z.boolean(),
});

// ─────────────────────────── points.spendGuide ──────────────────────────

/** Что вообще можно закрыть Баллами. Список замкнут на услуги нашего юрлица. */
export const GrantTargetTypeSchema = z.enum(["programme", "subscription", "course", "addon"]);

export const PointsSpendTargetSchema = z.object({
	key: z.string().min(1).max(128),
	serviceId: ServiceIdSchema,
	targetType: GrantTargetTypeSchema,
	/** Доля чека, которую закрывают Баллы. Поле SKU, дефолт — из констант. */
	maxSharePct: z.number().int().min(0).max(100),
	url: z.url(),
	available: z.boolean(),
});

export const PointsSpendGuideOutputSchema = z.object({
	targets: z.array(PointsSpendTargetSchema),
	balance: PointsBalanceSchema,
	worthMinor: MoneyMinorSchema.nonnegative(),
	currency: CurrencySchema,
	defaultSharePct: z.number().int().min(0).max(100),
});

// ─────────────────────── points.quoteGrant / createGrant ────────────────

/**
 * Область действия гранта — граница закрытого контура (инвариант №1 п. «г»).
 * `ownServicesOnly` — литерал `true`: значение `false` невыразимо в типе, поэтому
 * грант на чужую услугу (авиабилет, страховка, жильё третьей стороны) нельзя
 * даже сконструировать.
 */
export const GrantScopeSchema = z.object({
	targetType: GrantTargetTypeSchema,
	targetId: z.string().min(1).max(128),
	ownServicesOnly: z.literal(true),
});

export const QuoteGrantInputSchema = z.object({
	serviceId: ServiceIdSchema,
	scope: GrantScopeSchema,
	/**
	 * База погашения — ТОЛЬКО наша часть чека. Сторонние компоненты программы
	 * (авиабилет, страховка, жильё, визовые сборы) в `eligibleMinor` не входят.
	 */
	eligibleMinor: MoneyMinorSchema.nonnegative(),
	currency: CurrencySchema.default("USD"),
});

export const QuoteGrantOutputSchema = z.object({
	eligibleMinor: MoneyMinorSchema.nonnegative(),
	currency: CurrencySchema,
	/**
	 * Имя цели — то, что человек видит в заголовке модалки «Оплатить Баллами».
	 *
	 * Собирается ТОЛЬКО из наших данных: реестр сервисов (`ECOSYSTEM_SERVICES`)
	 * и тарифы IEC+ (`IEC_PLUS_PLAN_KEYS`). Из запроса сюда не попадает ни один
	 * символ — раньше подпись приходила параметром адресной строки, и любой
	 * человек мог прислать ссылку с НАШЕЙ модалкой и ЧУЖИМ текстом.
	 *
	 * `null` — имени у цели нет (программа, курс, дополнение: их каталог живёт в
	 * сервисе, а не у нас). Тогда витрина подписывает счёт типом цели и именем
	 * сервиса, а не выдумывает название.
	 *
	 * Значение — имя собственное («StudyHub», «IEC+ Pro»), поэтому оно не
	 * переводится и не нарушает правило «UI-тексты через ключи»: переводимая
	 * часть подписи собирается на клиенте.
	 */
	targetName: z.string().min(1).max(200).nullable(),
	/** Доля SKU (`max_points_share_pct`), дефолт — из констант. */
	sharePct: z.number().int().min(0).max(100),
	/** Потолок по доле: сколько денег/Баллов разрешено закрыть в принципе. */
	maxMinor: MoneyMinorSchema.nonnegative(),
	maxPoints: PointsBalanceSchema,
	balance: PointsBalanceSchema,
	/** Реально доступно = min(потолок доли, баланс) — это и есть предел слайдера. */
	availablePoints: PointsBalanceSchema,
	availableMinor: MoneyMinorSchema.nonnegative(),
	pointsPerUsd: z.number().int().positive(),
});

export const CreateGrantInputSchema = QuoteGrantInputSchema.extend({
	/** Идемпотентность обязательна: повтор возвращает тот же грант, не второй. */
	idempotencyKey: IdempotencyKeySchema,
	points: PointsPositiveSchema,
});

/** Жизненный цикл гранта. Возврат — всегда Баллами на счёт, никогда деньгами. */
export const GrantStateSchema = z.discriminatedUnion("state", [
	z.object({ state: z.literal("issued"), expiresAt: IsoDateTimeSchema }),
	z.object({ state: z.literal("applied"), appliedAt: IsoDateTimeSchema }),
	z.object({ state: z.literal("expired"), expiredAt: IsoDateTimeSchema }),
	z.object({
		state: z.literal("refunded"),
		refundedAt: IsoDateTimeSchema,
		refundedPoints: PointsPositiveSchema,
	}),
]);

export const GrantSchema = z.object({
	id: UuidV7Schema,
	serviceId: ServiceIdSchema,
	/** Номинал скидки в minor units — сервис применяет его к счёту как купон. */
	amountMinor: MoneyMinorSchema.positive(),
	currency: CurrencySchema,
	pointsSpent: PointsPositiveSchema,
	scope: GrantScopeSchema,
	state: GrantStateSchema,
	createdAt: IsoDateTimeSchema,
});

/**
 * Подпись купона — то, что человек передаёт сервису после выпуска гранта.
 *
 * ЗАЧЕМ ОНА В ОТВЕТЕ. Эндпоинты сервиса `POST /api/v1/grants/{verify,apply}`
 * требуют пару `{grantId, signature}`. Пока подписи не было в выходе процедуры,
 * взять её было НЕОТКУДА, и весь контур погашения Баллов в сервисах не работал:
 * гранты выпускались, Баллы списывались, через 24 часа `releaseExpiredGrants`
 * возвращал их обратно (аудит сверки документов, блокер №1).
 *
 * ЧТО ЭТА ПОДПИСЬ ДОКАЗЫВАЕТ И ЧЕГО НЕ ДОКАЗЫВАЕТ. Ключ подписи наш и сервисам
 * не выдаётся, поэтому проверить купон ОФЛАЙН сервис не может — и не должен:
 * применение купона меняет его статус в НАШЕЙ базе, значит сетевой вызов
 * неизбежен в любом случае. Подпись здесь решает другую задачу: без неё
 * `verify` отвечал бы «существует / не существует» на любой присланный
 * `grantId` и стал бы оракулом для перебора.
 */
export const GrantAssertionSchema = z.object({
	/** Значение поля `signature` в теле `verify` / `apply`. Формат `v1=<hex>`. */
	signature: z.string().min(8).max(256),
	/**
	 * Компактная форма `<grantId>.<signature>` — ею купон возвращается сервису
	 * параметром `?grant=` на его же адрес. Одна строка вместо двух параметров:
	 * сервису нечего перепутать местами, а нам нечего собирать двумя способами.
	 */
	token: z.string().min(16).max(512),
});

export const CreateGrantOutputSchema = z.object({
	grant: GrantSchema,
	/** Подписанный купон для сервиса. Без него грант применить нельзя. */
	assertion: GrantAssertionSchema,
	balance: PointsBalanceSchema,
	/** `true` — сработала идемпотентность, повторного списания не было. */
	duplicate: z.boolean(),
});

export const CancelGrantInputSchema = z.object({ grantId: UuidV7Schema });

export const CancelGrantOutputSchema = z.object({
	grant: GrantSchema,
	/** Неиспользованный грант возвращает Баллы — не деньги (план §4.3). */
	refundedPoints: PointsPositiveSchema,
	balance: PointsBalanceSchema,
});

// ───────────────────── admin.points.adjust / .reverse ───────────────────
// Схемы живут здесь, а не в контракте админки: форму записи ledger задаёт
// модуль-эмитент. Процедуры в namespace `points` под них НЕТ — единственный
// вход к ручной корректировке админский (см. `contracts/points/router.ts`).

/**
 * Ledger append-only (инвариант №11): процедур update/delete записи не существует.
 * Ручная корректировка — НОВАЯ строка: `kind='reverse'` при указанном
 * `reverseOfEntryId`, иначе `kind='adjust'`. Пишется также в `admin_audit_log`.
 */
export const AdminAdjustInputSchema = z.object({
	userSub: SubjectSchema,
	/** Знак задаёт направление; ноль бессмысленен и отклоняется. */
	amount: PointsAmountSchema.refine((value) => value !== 0, "Корректировка на ноль запрещена"),
	reasonCode: z.string().min(1).max(64),
	/** Обоснование для аудита — обязательно, решение ручное. */
	comment: z.string().min(3).max(1000),
	idempotencyKey: IdempotencyKeySchema,
	/** Указан — запись становится сторно конкретной строки ledger. */
	reverseOfEntryId: UuidV7Schema.nullish(),
});

export const AdminAdjustOutputSchema = z.object({
	entryId: UuidV7Schema,
	kind: PointsEntryKindSchema,
	balanceAfter: PointsBalanceSchema,
	createdAt: IsoDateTimeSchema,
});
